import React, { useState, useEffect } from "react";
import { Download, X } from "lucide-react";
import { createExport, getContactsBatch } from "../services/api";
import { downloadExport, exportStamp } from "../utils/exportFile";
import { GROUP_ORDER } from "../config/exportColumns";

// Group columns into sections, ordered by GROUP_ORDER then first-seen.
function groupColumns(columns) {
  const byGroup = {};
  const seen = [];
  columns.forEach((c) => {
    const g = c.group || "Other";
    if (!byGroup[g]) { byGroup[g] = []; seen.push(g); }
    byGroup[g].push(c);
  });
  const ordered = [...GROUP_ORDER.filter((g) => byGroup[g]), ...seen.filter((g) => !GROUP_ORDER.includes(g))];
  return ordered.map((g) => ({ group: g, cols: byGroup[g] }));
}

const F = "'Montserrat',sans-serif";

// Reusable "pick columns → export + log" modal. Driven by a `config` prop
// (null = closed). The caller supplies the rows, the column registry, scope and
// filename; the modal owns column selection, the download, and the audit log.
//
// config = {
//   title, columns:[{key,label,get}], defaultKeys:[key], rows:[...],
//   filenameBase, exportType, scope:{ show_id?, ite_event_id?, filters? }
// }
export default function ExportModal({ config, createdBy, onClose, onExported }) {
  const [selected, setSelected] = useState(new Set());
  const [oneEmailPerRecord, setOneEmailPerRecord] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!config) return;
    const initial = config.defaultKeys && config.defaultKeys.length
      ? config.defaultKeys
      : config.columns.map((c) => c.key);
    setSelected(new Set(initial));
    setOneEmailPerRecord(false);
    setExporting(false);
  }, [config]);

  if (!config) return null;

  const { title, columns, rows, filenameBase, exportType, scope = {} } = config;
  const allChecked = selected.size === columns.length;
  const chosen = columns.filter((c) => selected.has(c.key));
  const hasEmailCol = columns.some((c) => c.key === "email");
  const canExport = rows.length > 0 && chosen.length > 0 && !exporting;
  const sections = groupColumns(columns);

  const toggle = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  const toggleAll = () =>
    setSelected(allChecked ? new Set() : new Set(columns.map((c) => c.key)));
  const toggleGroup = (cols) =>
    setSelected((prev) => {
      const next = new Set(prev);
      const allOn = cols.every((c) => next.has(c.key));
      cols.forEach((c) => (allOn ? next.delete(c.key) : next.add(c.key)));
      return next;
    });

  const doExport = async () => {
    if (!canExport) return;
    setExporting(true);
    try {
      let finalRows = rows;

      if (oneEmailPerRecord && hasEmailCol) {
        // Fetch real contacts for every company in the export set, then flatten:
        // company with 2 generic/functional emails → 2 rows; no emails → excluded.
        const ids = rows.map((r) => String(r.id));
        const byCompany = await getContactsBatch(ids, scope.show_id ?? null);
        finalRows = rows.flatMap((r) => {
          const emails = (byCompany[String(r.id)] || []).filter(
            (c) => c.contact_kind === "email" &&
                   c.contact_type !== "personal_only" &&
                   c.contact_value
          );
          return emails.map((c) => ({ ...r, email: c.contact_value }));
        });
      }

      const filename = `${filenameBase}_${exportStamp()}.xlsx`;
      downloadExport({ columns: chosen, rows: finalRows, filename });

      // Audit log — best-effort, never blocks the download.
      createExport({
        export_type:  exportType,
        show_id:      scope.show_id ?? null,
        ite_event_id: scope.ite_event_id ?? null,
        filters_json: scope.filters ? JSON.stringify(scope.filters) : null,
        columns:      chosen.map((c) => c.key),
        record_count: finalRows.length,
        created_by:   createdBy,
      }).catch(() => {});

      onExported?.(finalRows.length);
      onClose();
    } catch (_) {
      setExporting(false);
    }
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(15,23,42,0.45)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:600, fontFamily:F }}
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background:"#fff", borderRadius:14, width:460, maxWidth:"92vw", padding:"24px", boxShadow:"0 24px 60px rgba(16,24,40,0.28)", display:"flex", flexDirection:"column", maxHeight:"88vh" }}>

        {/* Header */}
        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:6 }}>
          <div>
            <div style={{ fontSize:15, fontWeight:800, color:"#26262A" }}>{title || "Export to Excel"}</div>
            <div style={{ fontSize:11, color:"#AFABAB", marginTop:2 }}>
              {rows.length} {rows.length === 1 ? "row" : "rows"}
              {oneEmailPerRecord && <span style={{ color:"#E8431A" }}> · email only</span>}
              {" "}· choose the columns to include
            </div>
          </div>
          <button onClick={onClose}
            style={{ width:28, height:28, borderRadius:8, border:"1px solid #F4F4F5", background:"#F4F4F5", cursor:"pointer", color:"#818084", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <X size={14} />
          </button>
        </div>

        {/* One email per record toggle — shown only when Email column is in the set */}
        {hasEmailCol && (
          <button onClick={() => setOneEmailPerRecord((v) => !v)}
            style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 12px", borderRadius:9, marginBottom:4,
              border:`1.5px solid ${oneEmailPerRecord ? "#E8431A" : "#F4F4F5"}`,
              background: oneEmailPerRecord ? "#eff4ff" : "#F9F9F9", cursor:"pointer", fontFamily:F, textAlign:"left", width:"100%" }}>
            <span style={{ width:16, height:16, borderRadius:4, border:`2px solid ${oneEmailPerRecord ? "#E8431A" : "#AFABAB"}`,
              background: oneEmailPerRecord ? "#E8431A" : "#fff", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              {oneEmailPerRecord && <span style={{ color:"#fff", fontSize:11, fontWeight:900, lineHeight:1 }}>✓</span>}
            </span>
            <span style={{ flex:1 }}>
              <span style={{ fontSize:12, fontWeight: oneEmailPerRecord ? 700 : 500, color:"#26262A" }}>One email per record</span>
              <span style={{ fontSize:10, color:"#9CA0A5", marginLeft:6 }}>excludes companies without email — for ExProm DB upload</span>
            </span>
          </button>
        )}

        {/* Select all */}
        <button onClick={toggleAll}
          style={{ alignSelf:"flex-start", margin:"14px 0 8px", background:"none", border:"none", cursor:"pointer", fontFamily:F,
            fontSize:9, fontWeight:700, color:"#E8431A", textTransform:"uppercase", letterSpacing:0.5 }}>
          {allChecked ? "Clear all" : "Select all"} · {selected.size}/{columns.length}
        </button>

        {/* Column checklist — grouped into sections */}
        <div style={{ display:"flex", flexDirection:"column", gap:14, overflowY:"auto", paddingRight:4 }}>
          {sections.map(({ group, cols }) => {
            const groupAll = cols.every((c) => selected.has(c.key));
            const groupOn = cols.filter((c) => selected.has(c.key)).length;
            return (
              <div key={group}>
                {/* Section header */}
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                  <span style={{ fontSize:9, fontWeight:800, color:"#AFABAB", textTransform:"uppercase", letterSpacing:0.6 }}>
                    {group} · {groupOn}/{cols.length}
                  </span>
                  <button onClick={() => toggleGroup(cols)}
                    style={{ background:"none", border:"none", cursor:"pointer", fontFamily:F,
                      fontSize:9, fontWeight:700, color:"#E8431A", textTransform:"uppercase", letterSpacing:0.4 }}>
                    {groupAll ? "Clear" : "All"}
                  </button>
                </div>
                {/* Section columns */}
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {cols.map((c) => {
                    const sel = selected.has(c.key);
                    return (
                      <button key={c.key} onClick={() => toggle(c.key)}
                        style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 12px", borderRadius:9,
                          border:`1.5px solid ${sel ? "#E8431A" : "#F4F4F5"}`, background:sel ? "#eff4ff" : "#fff",
                          cursor:"pointer", fontFamily:F, textAlign:"left", width:"100%" }}>
                        <span style={{ width:16, height:16, borderRadius:4, border:`2px solid ${sel ? "#E8431A" : "#AFABAB"}`,
                          background:sel ? "#E8431A" : "#fff", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          {sel && <span style={{ color:"#fff", fontSize:11, fontWeight:900, lineHeight:1 }}>✓</span>}
                        </span>
                        <span style={{ flex:1, fontSize:12, fontWeight:sel ? 700 : 500, color:"#26262A" }}>{c.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div style={{ display:"flex", justifyContent:"flex-end", gap:8, marginTop:16 }}>
          <button onClick={onClose}
            style={{ padding:"9px 16px", borderRadius:8, border:"1px solid #D8DADC", background:"#fff", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:F, color:"#818084" }}>
            Cancel
          </button>
          <button onClick={doExport} disabled={!canExport}
            style={{ padding:"9px 20px", borderRadius:8, border:"none", fontSize:12, fontWeight:700,
              cursor: canExport ? "pointer" : "default", fontFamily:F, color:"#fff",
              background: canExport ? "#E8431A" : "#D8DADC", display:"flex", alignItems:"center", gap:6 }}>
            <Download size={13} />
            {exporting ? "Fetching contacts…" : `Export ${chosen.length > 0 ? `${chosen.length} cols` : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
